/**
 * Smart Reminder Hook
 * Tracks page load time and scroll position to determine when to show reminders
 * Shows reminder after delay OR after scroll threshold is reached
 */

import { useState, useEffect, useCallback } from 'react';

interface UseSmartReminderOptions {
  delay?: number; // milliseconds before showing (default: 5000)
  scrollThreshold?: number; // percentage 0-100 (default: 30)
  enabled?: boolean; // whether reminder is enabled (default: true)
}

interface UseSmartReminderResult {
  shouldShow: boolean;
  show: () => void;
  hide: () => void;
}

export function useSmartReminder(options: UseSmartReminderOptions = {}): UseSmartReminderResult {
  const {
    delay = 5000,
    scrollThreshold = 30,
    enabled = true,
  } = options;

  const [shouldShow, setShouldShow] = useState(false);
  const [forceShow, setForceShow] = useState(false);
  const [forceHide, setForceHide] = useState(false);

  // Track scroll position
  useEffect(() => {
    if (!enabled || forceHide || forceShow) {
      return;
    }

    let scrollPercentage = 0;
    let delayReached = false;

    // Check delay
    const delayTimer = setTimeout(() => {
      delayReached = true;
      if (scrollPercentage >= scrollThreshold || delayReached) {
        setShouldShow(true);
      }
    }, delay);

    // Track scroll
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      
      scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;

      // Show if threshold reached
      if (scrollPercentage >= scrollThreshold && !shouldShow) {
        setShouldShow(true);
      }
    };

    // Throttle scroll events
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      clearTimeout(delayTimer);
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [delay, scrollThreshold, enabled, shouldShow, forceShow, forceHide]);

  const show = useCallback(() => {
    setForceShow(true);
    setForceHide(false);
    setShouldShow(true);
  }, []);

  const hide = useCallback(() => {
    setForceHide(true);
    setForceShow(false);
    setShouldShow(false);
  }, []);

  return {
    shouldShow: enabled && (forceShow || shouldShow) && !forceHide,
    show,
    hide,
  };
}

